"use strict";
/**
 * Shared Doubt Service
 * Used by both ilearn (student) and iteach (teacher) apps.
 *
 * Usage:
 *   import { createDoubtService } from '@conceptwaves/shared-components';
 *   const doubtService = createDoubtService({ baseUrl, getToken });
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createDoubtService = void 0;
function buildHeaders(token) {
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
}
async function request(url, init) {
    const response = await fetch(url, init);
    if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
    }
    const data = await response.json();
    return data.data || data;
}
function createDoubtService(config) {
    const { baseUrl, getToken } = config;
    const headers = () => buildHeaders(getToken());
    return {
        /** Create a new doubt record */
        async createDoubt(payload) {
            return request(`${baseUrl}v1/doubts`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify(payload),
            });
        },
        /** Get doubts for a specific student */
        async getStudentDoubts(studentId, filters = {}) {
            const params = new URLSearchParams();
            if (filters.status)
                params.append('status', filters.status);
            if (filters.courseId)
                params.append('courseId', filters.courseId.toString());
            if (filters.limit)
                params.append('limit', filters.limit.toString());
            if (filters.offset)
                params.append('offset', filters.offset.toString());
            return request(`${baseUrl}v1/doubts/student/${studentId}?${params}`, {
                method: 'GET',
                headers: headers(),
            });
        },
        /** Update a doubt (feedback, status, conversation, etc.) */
        async updateDoubt(doubtId, updates) {
            return request(`${baseUrl}v1/doubts/${doubtId}`, {
                method: 'PATCH',
                headers: headers(),
                body: JSON.stringify(updates),
            });
        },
        /** Submit feedback on an AI response */
        async submitFeedback(doubtId, feedback, comment) {
            return request(`${baseUrl}v1/doubts/${doubtId}`, {
                method: 'PATCH',
                headers: headers(),
                body: JSON.stringify({ studentFeedback: feedback, feedbackComment: comment }),
            });
        },
        /** Escalate a doubt to a teacher */
        async escalateDoubt(doubtId, teacherId) {
            return request(`${baseUrl}v1/doubts/${doubtId}`, {
                method: 'PATCH',
                headers: headers(),
                body: JSON.stringify({
                    status: 'escalated',
                    escalatedAt: new Date().toISOString(),
                    escalatedTo: teacherId,
                }),
            });
        },
        /** Resolve a doubt with a teacher response */
        async resolveDoubt(doubtId, resolution) {
            return request(`${baseUrl}v1/doubts/${doubtId}/resolve`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify(resolution),
            });
        },
        /** Get doubts for a batch (teacher view) */
        async getBatchDoubts(batchId, filters = {}) {
            const params = new URLSearchParams();
            if (filters.status)
                params.append('status', filters.status);
            if (filters.courseId)
                params.append('courseId', filters.courseId.toString());
            if (filters.dateFrom)
                params.append('dateFrom', filters.dateFrom);
            if (filters.dateTo)
                params.append('dateTo', filters.dateTo);
            if (filters.limit)
                params.append('limit', filters.limit.toString());
            if (filters.offset)
                params.append('offset', filters.offset.toString());
            return request(`${baseUrl}v1/doubts/batch/${batchId}?${params}`, {
                method: 'GET',
                headers: headers(),
            });
        },
        /** Get student summaries for a batch */
        async getStudentSummaries(batchId) {
            return request(`${baseUrl}v1/doubts/batch/${batchId}/student-summaries`, {
                method: 'GET',
                headers: headers(),
            });
        },
        /** Get doubt statistics */
        async getStatistics(filters = {}) {
            const params = new URLSearchParams();
            if (filters.courseId)
                params.append('courseId', filters.courseId.toString());
            if (filters.dateFrom)
                params.append('dateFrom', filters.dateFrom);
            if (filters.dateTo)
                params.append('dateTo', filters.dateTo);
            return request(`${baseUrl}v1/doubts/statistics?${params}`, {
                method: 'GET',
                headers: headers(),
            });
        },
        /** Ask AI for doubt clarification (RAG-enabled) */
        async askAI(req) {
            return request(`${baseUrl}v1/gen-ai/doubt-clarification`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify(req),
            });
        },
    };
}
exports.createDoubtService = createDoubtService;
